// App secrets: the secure field Chat shows when an App needs an API key or token.
//
// Namespace `app-secrets`; owner: the App functions workstream (ui/workspace/app-secret-card.tsx).
// The value typed here goes straight to Poneva's secret store; no copy ever repeats it.
// Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Secret for {app}',
  sentTo: 'Sent only to {host}',
  valueLabel: 'Value of {name}',
  placeholder: 'Paste the value',
  alreadySet: 'Already set',
  save: 'Save',
  replace: 'Replace',
  saving: 'Saving…',
  saved: 'Saved. Your App can use it now.',
  ownerOnly: 'Only the App’s owner can set this.',
  unavailable: 'Secrets can’t be saved here yet.',
  failed: 'It couldn’t be saved. Try again.',
  privacy:
    'This field isn’t part of the chat. The model and the App’s code never see what you type.',
};

export type AppSecretsCopyKey = keyof typeof en;

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    title: 'Таен ключ за {app}',
    sentTo: 'Изпраща се само до {host}',
    valueLabel: 'Стойност на {name}',
    placeholder: 'Поставете стойността',
    alreadySet: 'Вече е зададена',
    save: 'Запазване',
    replace: 'Замяна',
    saving: 'Запазване…',
    saved: 'Запазено. Приложението ви вече може да я използва.',
    ownerOnly: 'Само собственикът на приложението може да задава това.',
    unavailable: 'Тайните още не могат да се запазват тук.',
    failed: 'Не можа да се запази. Опитайте отново.',
    privacy:
      'Това поле не е част от чата. Моделът и кодът на приложението никога не виждат въведеното от вас.',
  },
  hr: {
    title: 'Tajni ključ za aplikaciju {app}',
    sentTo: 'Šalje se samo na: {host}',
    valueLabel: 'Vrijednost: {name}',
    placeholder: 'Zalijepite vrijednost',
    alreadySet: 'Već postavljeno',
    save: 'Spremi',
    replace: 'Zamijeni',
    saving: 'Spremanje…',
    saved: 'Spremljeno. Vaša je aplikacija sada može upotrebljavati.',
    ownerOnly: 'Ovo može postaviti samo vlasnik aplikacije.',
    unavailable: 'Tajni se ključevi ovdje još ne mogu spremati.',
    failed: 'Nije bilo moguće spremiti. Pokušajte ponovno.',
    privacy: 'Ovo polje nije dio chata. Ni model ni kôd aplikacije nikad ne vide što upišete.',
  },
  cs: {
    title: 'Tajný klíč pro aplikaci {app}',
    sentTo: 'Odesílá se jen na: {host}',
    valueLabel: 'Hodnota: {name}',
    placeholder: 'Vložte hodnotu',
    alreadySet: 'Již nastaveno',
    save: 'Uložit',
    replace: 'Nahradit',
    saving: 'Ukládání…',
    saved: 'Uloženo. Vaše aplikace ho nyní může používat.',
    ownerOnly: 'Nastavit to může jen vlastník aplikace.',
    unavailable: 'Tajné klíče zde zatím nelze ukládat.',
    failed: 'Nepodařilo se uložit. Zkuste to znovu.',
    privacy: 'Toto pole není součástí chatu. Model ani kód aplikace nikdy nevidí, co zadáte.',
  },
  da: {
    title: 'Hemmelighed til {app}',
    sentTo: 'Sendes kun til {host}',
    valueLabel: 'Værdi af {name}',
    placeholder: 'Indsæt værdien',
    alreadySet: 'Allerede angivet',
    save: 'Gem',
    replace: 'Erstat',
    saving: 'Gemmer…',
    saved: 'Gemt. Din app kan bruge den nu.',
    ownerOnly: 'Kun appens ejer kan angive dette.',
    unavailable: 'Hemmeligheder kan ikke gemmes her endnu.',
    failed: 'Det kunne ikke gemmes. Prøv igen.',
    privacy:
      'Dette felt er ikke en del af chatten. Modellen og appens kode ser aldrig, hvad du skriver.',
  },
  nl: {
    title: 'Geheim voor {app}',
    sentTo: 'Alleen verzonden naar {host}',
    valueLabel: 'Waarde van {name}',
    placeholder: 'Plak de waarde',
    alreadySet: 'Al ingesteld',
    save: 'Opslaan',
    replace: 'Vervangen',
    saving: 'Opslaan…',
    saved: 'Opgeslagen. Je app kan het nu gebruiken.',
    ownerOnly: 'Alleen de eigenaar van de app kan dit instellen.',
    unavailable: 'Geheimen kunnen hier nog niet worden opgeslagen.',
    failed: 'Opslaan is niet gelukt. Probeer het opnieuw.',
    privacy:
      'Dit veld maakt geen deel uit van de chat. Het model en de code van de app zien nooit wat je typt.',
  },
  en: {
    title: 'Secret for {app}',
    sentTo: 'Sent only to {host}',
    valueLabel: 'Value of {name}',
    placeholder: 'Paste the value',
    alreadySet: 'Already set',
    save: 'Save',
    replace: 'Replace',
    saving: 'Saving…',
    saved: 'Saved. Your App can use it now.',
    ownerOnly: 'Only the App’s owner can set this.',
    unavailable: 'Secrets can’t be saved here yet.',
    failed: 'It couldn’t be saved. Try again.',
    privacy:
      'This field isn’t part of the chat. The model and the App’s code never see what you type.',
  },
  et: {
    title: 'Saladus rakendusele {app}',
    sentTo: 'Saadetakse ainult serverisse {host}',
    valueLabel: 'Väärtus: {name}',
    placeholder: 'Kleebi väärtus',
    alreadySet: 'Juba määratud',
    save: 'Salvesta',
    replace: 'Asenda',
    saving: 'Salvestamine…',
    saved: 'Salvestatud. Sinu rakendus saab seda nüüd kasutada.',
    ownerOnly: 'Seda saab määrata ainult rakenduse omanik.',
    unavailable: 'Saladusi ei saa siin veel salvestada.',
    failed: 'Salvestamine ebaõnnestus. Proovi uuesti.',
    privacy:
      'See väli ei kuulu vestlusesse. Mudel ja rakenduse kood ei näe kunagi, mida sa sisestad.',
  },
  fi: {
    title: 'Salaisuus sovellukselle {app}',
    sentTo: 'Lähetetään vain palvelimelle {host}',
    valueLabel: 'Arvo: {name}',
    placeholder: 'Liitä arvo',
    alreadySet: 'Jo asetettu',
    save: 'Tallenna',
    replace: 'Korvaa',
    saving: 'Tallennetaan…',
    saved: 'Tallennettu. Sovelluksesi voi käyttää sitä nyt.',
    ownerOnly: 'Vain sovelluksen omistaja voi asettaa tämän.',
    unavailable: 'Salaisuuksia ei voi vielä tallentaa täällä.',
    failed: 'Tallennus ei onnistunut. Yritä uudelleen.',
    privacy:
      'Tämä kenttä ei ole osa keskustelua. Malli ja sovelluksen koodi eivät koskaan näe kirjoittamaasi.',
  },
  fr: {
    title: 'Secret pour {app}',
    sentTo: 'Envoyé uniquement à {host}',
    valueLabel: 'Valeur de {name}',
    placeholder: 'Collez la valeur',
    alreadySet: 'Déjà défini',
    save: 'Enregistrer',
    replace: 'Remplacer',
    saving: 'Enregistrement…',
    saved: 'Enregistré. Votre application peut l’utiliser maintenant.',
    ownerOnly: 'Seul le propriétaire de l’application peut le définir.',
    unavailable: 'Les secrets ne peuvent pas encore être enregistrés ici.',
    failed: 'L’enregistrement a échoué. Réessayez.',
    privacy:
      'Ce champ ne fait pas partie du chat. Le modèle et le code de l’application ne voient jamais ce que vous saisissez.',
  },
  de: {
    title: 'Geheimnis für {app}',
    sentTo: 'Wird nur an {host} gesendet',
    valueLabel: 'Wert von {name}',
    placeholder: 'Wert einfügen',
    alreadySet: 'Bereits festgelegt',
    save: 'Speichern',
    replace: 'Ersetzen',
    saving: 'Wird gespeichert…',
    saved: 'Gespeichert. Ihre App kann es jetzt verwenden.',
    ownerOnly: 'Nur die verantwortliche Person der App kann dies festlegen.',
    unavailable: 'Geheimnisse können hier noch nicht gespeichert werden.',
    failed: 'Es konnte nicht gespeichert werden. Versuchen Sie es erneut.',
    privacy:
      'Dieses Feld ist nicht Teil des Chats. Das Modell und der Code der App sehen nie, was Sie eingeben.',
  },
  el: {
    title: 'Μυστικό για το {app}',
    sentTo: 'Αποστέλλεται μόνο στο {host}',
    valueLabel: 'Τιμή του {name}',
    placeholder: 'Επικολλήστε την τιμή',
    alreadySet: 'Έχει ήδη οριστεί',
    save: 'Αποθήκευση',
    replace: 'Αντικατάσταση',
    saving: 'Αποθήκευση…',
    saved: 'Αποθηκεύτηκε. Η Εφαρμογή σας μπορεί πλέον να το χρησιμοποιήσει.',
    ownerOnly: 'Μόνο ο κάτοχος της Εφαρμογής μπορεί να το ορίσει.',
    unavailable: 'Τα μυστικά δεν μπορούν ακόμη να αποθηκευτούν εδώ.',
    failed: 'Δεν ήταν δυνατή η αποθήκευση. Δοκιμάστε ξανά.',
    privacy:
      'Αυτό το πεδίο δεν αποτελεί μέρος της συνομιλίας. Το μοντέλο και ο κώδικας της Εφαρμογής δεν βλέπουν ποτέ ό,τι πληκτρολογείτε.',
  },
  hu: {
    title: 'Titkos kulcs ehhez: {app}',
    sentTo: 'Csak ide küldjük: {host}',
    valueLabel: 'Érték: {name}',
    placeholder: 'Illeszd be az értéket',
    alreadySet: 'Már beállítva',
    save: 'Mentés',
    replace: 'Csere',
    saving: 'Mentés…',
    saved: 'Elmentve. Az alkalmazásod mostantól használhatja.',
    ownerOnly: 'Ezt csak az alkalmazás tulajdonosa állíthatja be.',
    unavailable: 'Titkos kulcsokat itt még nem lehet menteni.',
    failed: 'Nem sikerült menteni. Próbáld újra.',
    privacy:
      'Ez a mező nem része a csevegésnek. Sem a modell, sem az alkalmazás kódja nem látja, amit beírsz.',
  },
  ga: {
    title: 'Rún don aip {app}',
    sentTo: 'Seoltar chuig {host} amháin',
    valueLabel: 'Luach {name}',
    placeholder: 'Greamaigh an luach',
    alreadySet: 'Socraithe cheana',
    save: 'Sábháil',
    replace: 'Ionadaigh',
    saving: 'Á shábháil…',
    saved: 'Sábháilte. Is féidir le d’aip é a úsáid anois.',
    ownerOnly: 'Ní féidir ach le húinéir na haipe é seo a shocrú.',
    unavailable: 'Ní féidir rúin a shábháil anseo fós.',
    failed: 'Níorbh fhéidir é a shábháil. Bain triail eile as.',
    privacy:
      'Níl an réimse seo mar chuid den chomhrá. Ní fheiceann an tsamhail ná cód na haipe riamh an rud a chlóscríobhann tú.',
  },
  it: {
    title: 'Segreto per {app}',
    sentTo: 'Inviato solo a {host}',
    valueLabel: 'Valore di {name}',
    placeholder: 'Incolla il valore',
    alreadySet: 'Già impostato',
    save: 'Salva',
    replace: 'Sostituisci',
    saving: 'Salvataggio…',
    saved: 'Salvato. La tua app può usarlo subito.',
    ownerOnly: 'Solo il proprietario dell’app può impostarlo.',
    unavailable: 'Qui non è ancora possibile salvare i segreti.',
    failed: 'Non è stato possibile salvarlo. Riprova.',
    privacy:
      'Questo campo non fa parte della chat. Il modello e il codice dell’app non vedono mai ciò che scrivi.',
  },
  lv: {
    title: 'Noslēpums lietotnei {app}',
    sentTo: 'Tiek sūtīts tikai uz {host}',
    valueLabel: '{name} vērtība',
    placeholder: 'Ielīmējiet vērtību',
    alreadySet: 'Jau iestatīts',
    save: 'Saglabāt',
    replace: 'Aizstāt',
    saving: 'Notiek saglabāšana…',
    saved: 'Saglabāts. Jūsu lietotne to tagad var izmantot.',
    ownerOnly: 'Šo var iestatīt tikai lietotnes īpašnieks.',
    unavailable: 'Noslēpumus šeit vēl nevar saglabāt.',
    failed: 'Neizdevās saglabāt. Mēģiniet vēlreiz.',
    privacy:
      'Šis lauks nav tērzēšanas daļa. Modelis un lietotnes kods nekad neredz to, ko ierakstāt.',
  },
  lt: {
    title: 'Programos {app} paslaptis',
    sentTo: 'Siunčiama tik į {host}',
    valueLabel: '{name} reikšmė',
    placeholder: 'Įklijuokite reikšmę',
    alreadySet: 'Jau nustatyta',
    save: 'Išsaugoti',
    replace: 'Pakeisti',
    saving: 'Išsaugoma…',
    saved: 'Išsaugota. Jūsų programa dabar gali ją naudoti.',
    ownerOnly: 'Tai gali nustatyti tik programos savininkas.',
    unavailable: 'Paslapčių čia dar negalima išsaugoti.',
    failed: 'Nepavyko išsaugoti. Bandykite dar kartą.',
    privacy:
      'Šis laukas nėra pokalbio dalis. Modelis ir programos kodas niekada nemato to, ką įvedate.',
  },
  mt: {
    title: 'Sigriet għal {app}',
    sentTo: 'Mibgħut biss lil {host}',
    valueLabel: 'Valur ta’ {name}',
    placeholder: 'Waħħal il-valur',
    alreadySet: 'Diġà ssettjat',
    save: 'Aħżen',
    replace: 'Ibdel',
    saving: 'Qed jinħażen…',
    saved: 'Inħażen. L-app tiegħek tista’ tużah issa.',
    ownerOnly: 'Is-sid tal-app biss jista’ jissettjah.',
    unavailable: 'Is-sigrieti għadhom ma jistgħux jinħażnu hawnhekk.',
    failed: 'Ma setax jinħażen. Erġa’ pprova.',
    privacy:
      'Dan il-qasam mhuwiex parti miċ-chat. Il-mudell u l-kodiċi tal-app qatt ma jaraw dak li tikteb.',
  },
  pl: {
    title: 'Sekret dla aplikacji {app}',
    sentTo: 'Wysyłany tylko do: {host}',
    valueLabel: 'Wartość: {name}',
    placeholder: 'Wklej wartość',
    alreadySet: 'Już ustawiono',
    save: 'Zapisz',
    replace: 'Zastąp',
    saving: 'Zapisywanie…',
    saved: 'Zapisano. Twoja aplikacja może teraz z niego korzystać.',
    ownerOnly: 'Tylko właściciel aplikacji może to ustawić.',
    unavailable: 'Sekretów nie można tu jeszcze zapisywać.',
    failed: 'Nie udało się zapisać. Spróbuj ponownie.',
    privacy:
      'To pole nie jest częścią czatu. Ani model, ani kod aplikacji nigdy nie widzą tego, co wpisujesz.',
  },
  pt: {
    title: 'Segredo de {app}',
    sentTo: 'Enviado somente para {host}',
    valueLabel: 'Valor de {name}',
    placeholder: 'Cole o valor',
    alreadySet: 'Já definido',
    save: 'Salvar',
    replace: 'Substituir',
    saving: 'Salvando…',
    saved: 'Salvo. Seu aplicativo já pode usá-lo.',
    ownerOnly: 'Somente o proprietário do aplicativo pode defini-lo.',
    unavailable: 'Ainda não é possível salvar segredos aqui.',
    failed: 'Não foi possível salvar. Tente novamente.',
    privacy:
      'Este campo não faz parte do chat. O modelo e o código do aplicativo nunca veem o que você digita.',
  },
  ro: {
    title: 'Secret pentru {app}',
    sentTo: 'Trimis doar către {host}',
    valueLabel: 'Valoarea {name}',
    placeholder: 'Lipește valoarea',
    alreadySet: 'Deja setat',
    save: 'Salvează',
    replace: 'Înlocuiește',
    saving: 'Se salvează…',
    saved: 'Salvat. Aplicația ta îl poate folosi acum.',
    ownerOnly: 'Doar proprietarul aplicației îl poate seta.',
    unavailable: 'Secretele nu pot fi salvate încă aici.',
    failed: 'Nu a putut fi salvat. Încearcă din nou.',
    privacy:
      'Acest câmp nu face parte din chat. Modelul și codul aplicației nu văd niciodată ce scrii.',
  },
  sk: {
    title: 'Tajný kľúč pre aplikáciu {app}',
    sentTo: 'Odosiela sa iba na: {host}',
    valueLabel: 'Hodnota: {name}',
    placeholder: 'Vložte hodnotu',
    alreadySet: 'Už nastavené',
    save: 'Uložiť',
    replace: 'Nahradiť',
    saving: 'Ukladá sa…',
    saved: 'Uložené. Vaša aplikácia ho teraz môže používať.',
    ownerOnly: 'Nastaviť to môže iba vlastník aplikácie.',
    unavailable: 'Tajné kľúče sa tu zatiaľ nedajú ukladať.',
    failed: 'Nepodarilo sa uložiť. Skúste to znova.',
    privacy: 'Toto pole nie je súčasťou chatu. Model ani kód aplikácie nikdy nevidia, čo zadáte.',
  },
  sl: {
    title: 'Skrivni ključ za aplikacijo {app}',
    sentTo: 'Pošlje se samo na: {host}',
    valueLabel: 'Vrednost: {name}',
    placeholder: 'Prilepite vrednost',
    alreadySet: 'Že nastavljeno',
    save: 'Shrani',
    replace: 'Zamenjaj',
    saving: 'Shranjevanje…',
    saved: 'Shranjeno. Vaša aplikacija ga zdaj lahko uporablja.',
    ownerOnly: 'To lahko nastavi samo lastnik aplikacije.',
    unavailable: 'Skrivnih ključev tu še ni mogoče shranjevati.',
    failed: 'Ni bilo mogoče shraniti. Poskusite znova.',
    privacy:
      'To polje ni del klepeta. Niti model niti koda aplikacije nikoli ne vidita, kaj vnesete.',
  },
  es: {
    title: 'Secreto de {app}',
    sentTo: 'Se envía solo a {host}',
    valueLabel: 'Valor de {name}',
    placeholder: 'Pega el valor',
    alreadySet: 'Ya definido',
    save: 'Guardar',
    replace: 'Reemplazar',
    saving: 'Guardando…',
    saved: 'Guardado. Tu app ya puede usarlo.',
    ownerOnly: 'Solo el propietario de la app puede definirlo.',
    unavailable: 'Aún no se pueden guardar secretos aquí.',
    failed: 'No se pudo guardar. Inténtalo de nuevo.',
    privacy:
      'Este campo no forma parte del chat. El modelo y el código de la app nunca ven lo que escribes.',
  },
  sv: {
    title: 'Hemlighet för {app}',
    sentTo: 'Skickas bara till {host}',
    valueLabel: 'Värdet för {name}',
    placeholder: 'Klistra in värdet',
    alreadySet: 'Redan angiven',
    save: 'Spara',
    replace: 'Ersätt',
    saving: 'Sparar…',
    saved: 'Sparad. Din app kan använda den nu.',
    ownerOnly: 'Bara appens ägare kan ange den här.',
    unavailable: 'Hemligheter kan inte sparas här ännu.',
    failed: 'Den kunde inte sparas. Försök igen.',
    privacy:
      'Det här fältet är inte en del av chatten. Varken modellen eller appens kod ser det du skriver.',
  },
};
export default copy;
